/**
 * The worked example, wired to the wizard's own state
 * (ATTEST-UX-AND-DEMO-READINESS-1 item 2).
 *
 * Split out of `LetterGenerator` under the 400-line rule in
 * `docs/FILE_SIZE_POLICY.md`, alongside `useLetterCalls` and `useFieldAnchor`.
 * The page keeps the four `useState` calls; this keeps the two rules about when
 * the example may be offered and what it writes.
 *
 * `demoLetter.ts` holds the mapping and the fixture; this holds only the
 * wiring, so nothing about *which* values the example uses is decided here.
 */
import { useCallback, useMemo } from 'react'

import type { LetterTypeSummary } from '@/components/attest/api'
import type { ClassifyFormState } from '@/components/attest/classifyQuestions'
import { DEMO_TYPE_ID, demoPrefill, isPristine } from '@/components/attest/demoLetter'
import type { IssuerFormState, LetterFormState } from '@/components/attest/formState'

interface DemoPrefillArgs {
  letterTypes: LetterTypeSummary[]
  classify: ClassifyFormState
  form: LetterFormState
  setTypeId: (id: string) => void
  setBasis: (key: string) => void
  setClassify: (next: ClassifyFormState) => void
  setForm: (next: LetterFormState) => void
  setIssuer: (next: IssuerFormState) => void
}

/**
 * @returns the handler to fill the wizard, or `undefined` when the example must
 * not be offered — which the step renders as no button at all.
 *
 * Two conditions, both necessary:
 *
 * - **Nothing has been typed yet.** Loading the example overwrites all four
 *   forms and there is no undo, so the affordance removes itself rather than
 *   asking a CPA mid-letter to confirm they did not mean it.
 * - **The server publishes the type it needs.** A type withdrawn from the
 *   registry, or one that has not cleared legal review, would otherwise prefill
 *   a form pointing at something this deployment cannot build.
 *
 * The write itself is one handler because it must be atomic: a per-step prefill
 * would leave the wizard half-filled if the CPA navigated away between two of
 * them. It is still only a prefill — `/classify` and `/letter` run every gate
 * against the result exactly as they would against typed input.
 */
export function useDemoPrefill({
  letterTypes, classify, form,
  setTypeId, setBasis, setClassify, setForm, setIssuer,
}: DemoPrefillArgs): (() => void) | undefined {
  const loadDemo = useCallback(() => {
    const prefill = demoPrefill()
    setTypeId(prefill.typeId)
    setBasis(prefill.basis)
    setClassify(prefill.classify)
    setForm(prefill.letter)
    setIssuer(prefill.issuer)
  }, [setTypeId, setBasis, setClassify, setForm, setIssuer])

  const offerable = useMemo(
    () => letterTypes.some(t => t.type_id === DEMO_TYPE_ID) && isPristine(classify, form),
    [letterTypes, classify, form],
  )

  return offerable ? loadDemo : undefined
}
